import React, { PureComponent } from "react";
import * as Icon from "react-feather";

import { operatorsForType, defaultValueFor } from "./operatorDefinitions";
import FilterValueInput from "./FilterValueInput";
import FilterCombobox from "./FilterCombobox";

/**
 * One filter rule row inside a group.
 *
 * Props:
 *   rule       : { _id, type:'rule', field, op, value }
 *   schema     : { fields: { [key]: { key, label, type, options?, group? } } }
 *   ctx        : passed through to FilterValueInput for option resolvers
 *   onChange   : (patchedRule) => void
 *   onRemove   : () => void
 */
class FilterRule extends PureComponent {
	onFieldChange = key => {
		const fieldDef = key ? this.props.schema.fields[key] : null;
		const ops = fieldDef ? operatorsForType(fieldDef.type) : [];
		const firstOp = ops.length ? ops[0].op : null;
		this.props.onChange({
			...this.props.rule,
			field: key,
			op: firstOp,
			value: firstOp && fieldDef ? defaultValueFor(fieldDef.type, firstOp) : null,
		});
	};

	onOpChange = e => {
		const op = e.target.value;
		const fieldDef = this.props.schema.fields[this.props.rule.field];
		const type = fieldDef ? fieldDef.type : "text";
		this.props.onChange({
			...this.props.rule,
			op,
			value: defaultValueFor(type, op),
		});
	};

	onValueChange = value => {
		this.props.onChange({ ...this.props.rule, value });
	};

	_groupedFields() {
		const fields = this.props.schema.fields || {};
		const list = Object.values(fields);
		const groups = new Map();
		const noGroup = [];
		for (const f of list) {
			if (f.group) {
				if (!groups.has(f.group)) groups.set(f.group, []);
				groups.get(f.group).push(f);
			} else {
				noGroup.push(f);
			}
		}
		return { groups, noGroup };
	}

	_fieldOptions() {
		const { groups, noGroup } = this._groupedFields();
		const opts = noGroup.map(f => ({ value: f.key, label: f.label }));
		for (const [groupName, items] of groups.entries()) {
			for (const f of items) opts.push({ value: f.key, label: f.label, group: groupName });
		}
		return opts;
	}

	render() {
		const { rule, schema, ctx } = this.props;
		const fieldDef = rule.field ? schema.fields[rule.field] : null;
		const ops = fieldDef ? operatorsForType(fieldDef.type) : [];

		return (
			<div className="filter-rule">
				<div className="filter-rule__field-cell">
					<FilterCombobox
						options={this._fieldOptions()}
						value={rule.field || null}
						placeholder="Select field…"
						onChange={this.onFieldChange}
					/>
				</div>

				<select
					className="filter-rule__op"
					value={rule.op || ""}
					onChange={this.onOpChange}
					disabled={!fieldDef}
				>
					{!rule.op && <option value="">—</option>}
					{ops.map(o => (
						<option key={o.op} value={o.op}>
							{o.label}
						</option>
					))}
				</select>

				<div className="filter-rule__value">
					{fieldDef && rule.op && (
						<FilterValueInput
							field={fieldDef}
							op={rule.op}
							value={rule.value}
							ctx={ctx}
							onChange={this.onValueChange}
						/>
					)}
				</div>

				<button
					type="button"
					className="filter-rule__remove"
					onClick={this.props.onRemove}
					title="Remove this rule"
				>
					<Icon.X size={13} />
				</button>
			</div>
		);
	}
}

export default FilterRule;
